import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, ClipboardCheck, Signpost, MessageCircle, Lightbulb, BookOpenText, DollarSign, HelpCircle, FileText } from 'lucide-react';

const PAGES = {
  tests: {
    icon: ClipboardCheck,
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    paths: { sv: '/teoriprov', en: '/en/tests', ar: '/ar/ikhtibarat', tr: '/tr/testler', fa: '/fa/azmoon-ha', so: '/so/imtixaanada', de: '/de/tests', es: '/es/examenes', pl: '/pl/testy' },
    labels: { sv: 'Övningsprov', en: 'Practice Tests', ar: 'اختبارات تدريبية', tr: 'Deneme Sınavları', fa: 'آزمون‌ها', so: 'Imtixaanada', de: 'Übungstests', es: 'Exámenes', pl: 'Testy' },
    desc: { sv: '33 övningsprov med förklaringar', en: '33 practice tests with explanations', ar: '33 امتحان تجريبي مع الشرح', tr: '24 açıklamalı deneme sınavı', fa: '33 آزمون با توضیحات', so: '33 imtixaan sharaxaad leh', de: '33 Tests mit Erklärungen', es: '33 tests con explicaciones', pl: '33 testów z wyjaśnieniami' }
  },
  lessons: {
    icon: BookOpen,
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    paths: { sv: '/teorilektioner', en: '/en/lessons', ar: '/ar/doross', tr: '/tr/dersler', fa: '/fa/dars-ha', so: '/so/casharada', de: '/de/lektionen', es: '/es/lecciones', pl: '/pl/lekcje' },
    labels: { sv: 'Teorilektioner', en: 'Theory Lessons', ar: 'الدروس النظرية', tr: 'Dersler', fa: 'درس‌ها', so: 'Casharada', de: 'Lektionen', es: 'Lecciones', pl: 'Lekcje' },
    desc: { sv: '26 lektioner om trafikregler', en: '26 lessons on traffic rules', ar: '26 درس عن قواعد المرور', tr: '26 trafik kuralları dersi', fa: '26 درس قوانین راهنمایی', so: '26 casharro xeerarka', de: '26 Lektionen über Verkehrsregeln', es: '26 lecciones de normas', pl: '26 lekcji o przepisach' }
  },
  signs: {
    icon: Signpost,
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    paths: { sv: '/trafikmarken', en: '/en/traffic-signs', ar: '/ar/isharat-almroor', tr: '/tr/trafik-isaretleri', fa: '/fa/alamat-rahnamaii', so: '/so/calaamadaha-taraafikada', de: '/de/verkehrszeichen', es: '/es/senales-trafico', pl: '/pl/znaki-drogowe' },
    labels: { sv: 'Trafikmärken', en: 'Traffic Signs', ar: 'إشارات المرور', tr: 'Trafik İşaretleri', fa: 'تابلوها', so: 'Calaamadaha', de: 'Verkehrszeichen', es: 'Señales', pl: 'Znaki drogowe' },
    desc: { sv: '359 trafikmärken med förklaringar', en: '359 traffic signs explained', ar: '359 إشارة مرور مع الشرح', tr: '359 trafik işareti', fa: '365 تابلو با توضیحات', so: '359 calaamadood', de: '359 Verkehrszeichen', es: '359 señales de tráfico', pl: '359 znaków drogowych' }
  },
  words: {
    icon: BookOpenText,
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    paths: { sv: '/viktiga-ord', en: '/en/important-words', ar: '/ar/kalimat-hama', tr: '/tr/onemli-kelimeler', fa: '/fa/kalamaat-mohem', so: '/so/erayada-muhiimka', de: '/de/wichtige-worter', es: '/es/palabras-importantes', pl: '/pl/wazne-slowa' },
    labels: { sv: 'Viktiga Ord', en: 'Important Words', ar: 'كلمات هامة', tr: 'Önemli Kelimeler', fa: 'کلمات مهم', so: 'Erayada', de: 'Wichtige Wörter', es: 'Palabras Clave', pl: 'Ważne Słowa' },
    desc: { sv: '69 viktiga begrepp för teoriprovet', en: '69 key terms for the theory test', ar: '69 مصطلح أساسي للتيوري', tr: '69 önemli terim', fa: '69 اصطلاح کلیدی', so: '69 eray muhiim', de: '69 wichtige Begriffe', es: '69 términos clave', pl: '69 kluczowych pojęć' }
  },
  tips: {
    icon: Lightbulb,
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    paths: { sv: '/snabba-rad', en: '/en/quick-tips', ar: '/ar/nasaeh-sareea', tr: '/tr/hizli-ipuclari', fa: '/fa/nookat-sari', so: '/so/talooyinka-degdegga', de: '/de/schnelle-tipps', es: '/es/consejos-rapidos', pl: '/pl/szybkie-porady' },
    labels: { sv: 'Snabba Råd', en: 'Quick Tips', ar: 'نصائح سريعة', tr: 'Hızlı İpuçları', fa: 'نکات سریع', so: 'Talooyinka', de: 'Schnelle Tipps', es: 'Consejos', pl: 'Porady' },
    desc: { sv: 'Tips för att klara provet', en: 'Tips to pass the test', ar: 'نصائح للنجاح بالامتحان', tr: 'Sınavı geçme ipuçları', fa: 'نکات قبولی', so: 'Talooyinka guusha', de: 'Tipps zum Bestehen', es: 'Consejos para aprobar', pl: 'Wskazówki do egzaminu' }
  },
  assistant: {
    icon: MessageCircle,
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    paths: { sv: '/ai-assistent', en: '/en/assistant', ar: '/ar/almosaed', tr: '/tr/asistan', fa: '/fa/dastiar', so: '/so/kaaliye', de: '/de/assistent', es: '/es/asistente', pl: '/pl/asystent' },
    labels: { sv: 'AI-Assistent', en: 'AI Assistant', ar: 'المساعد الذكي', tr: 'AI Asistan', fa: 'دستیار هوشمند', so: 'Kaaliyaha AI', de: 'KI-Assistent', es: 'Asistente IA', pl: 'Asystent AI' },
    desc: { sv: 'Ställ frågor om teori', en: 'Ask theory questions', ar: 'اسأل عن التيوري', tr: 'Teori soruları sorun', fa: 'سوالات تئوری بپرسید', so: 'Su\'aalo weydii', de: 'Theoriefragen stellen', es: 'Haz preguntas de teoría', pl: 'Zadaj pytania o teorii' }
  },
  pricing: {
    icon: DollarSign,
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    paths: { sv: '/priser', en: '/en/pricing', ar: '/ar/alasaar', tr: '/tr/fiyatlar', fa: '/fa/gheymat-ha', so: '/so/qiimaha', de: '/de/preise', es: '/es/precios', pl: '/pl/cennik' },
    labels: { sv: 'Priser', en: 'Pricing', ar: 'الأسعار', tr: 'Fiyatlar', fa: 'قیمت‌ها', so: 'Qiimaha', de: 'Preise', es: 'Precios', pl: 'Cennik' },
    desc: { sv: 'Börja gratis, uppgradera när du vill', en: 'Start free, upgrade anytime', ar: 'ابدأ مجاناً، اشترك متى تريد', tr: 'Ücretsiz başla', fa: 'رایگان شروع کنید', so: 'Bilaash bilow', de: 'Kostenlos starten', es: 'Empieza gratis', pl: 'Zacznij za darmo' }
  },
  'how-to': {
    icon: HelpCircle,
    color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
    paths: { sv: '/hur-man-anvander', en: '/en/how-to-use', ar: '/ar/tariqat-alistkhdam', tr: '/tr/nasil-kullanilir', fa: '/fa/rahnamaye-estefade', so: '/so/sida-loo-isticmaalo', de: '/de/anleitung', es: '/es/como-usar', pl: '/pl/jak-korzystac' },
    labels: { sv: 'Hur man använder', en: 'How to Use', ar: 'طريقة الاستخدام', tr: 'Nasıl Kullanılır', fa: 'نحوه استفاده', so: 'Sida loo isticmaalo', de: 'Anleitung', es: 'Cómo usar', pl: 'Jak korzystać' },
    desc: { sv: 'Guide för nya användare', en: 'Guide for new users', ar: 'دليل المستخدم الجديد', tr: 'Yeni kullanıcı rehberi', fa: 'راهنمای کاربران', so: 'Hagaha cusub', de: 'Anleitung für Anfänger', es: 'Guía para nuevos usuarios', pl: 'Przewodnik dla nowych' }
  },
  articles: {
    icon: FileText,
    color: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
    paths: { sv: '/artiklar', en: '/en/articles', ar: '/ar/maqalat', tr: '/tr/makaleler', fa: '/fa/maqalat', so: '/so/maqaallada', de: '/de/artikel', es: '/es/articulos', pl: '/pl/artykuly' },
    labels: { sv: 'Artiklar', en: 'Articles', ar: 'مقالات', tr: 'Makaleler', fa: 'مقالات', so: 'Maqaallada', de: 'Artikel', es: 'Artículos', pl: 'Artykuły' },
    desc: { sv: 'Tips och råd för teoriprovet', en: 'Tips and advice for the theory test', ar: 'نصائح ومقالات للنجاح', tr: 'Sınav ipuçları ve tavsiyeler', fa: 'نکات و مشاوره آزمون', so: 'Talooyinka imtixaanka', de: 'Tipps für die Prüfung', es: 'Consejos para el examen', pl: 'Porady do egzaminu' }
  }
};

const sectionTitles = {
  sv: 'Utforska mer', en: 'Explore More', ar: 'اكتشف المزيد',
  tr: 'Daha Fazlasını Keşfedin', fa: 'بیشتر کاوش کنید', so: 'Baro wax badan',
  de: 'Mehr entdecken', es: 'Descubre más', pl: 'Odkryj więcej'
};

const RelatedPages = ({ currentPage, maxItems = 4 }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'sv';
  const isRTL = lang === 'ar' || lang === 'fa';

  const pageKeys = Object.keys(PAGES).filter(k => k !== currentPage);
  const displayed = pageKeys.slice(0, maxItems);

  return (
    <section className="mt-12 mb-8" dir={isRTL ? 'rtl' : 'ltr'} data-testid="related-pages">
      <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-4">
        {sectionTitles[lang] || sectionTitles.en}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {displayed.map(key => {
          const p = PAGES[key];
          const Icon = p.icon;
          return (
            <button
              key={key}
              onClick={() => navigate(p.paths[lang] || p.paths.sv)}
              className="group flex flex-col items-start gap-2 p-4 rounded-xl bg-white dark:bg-[#18181b] border border-slate-200 dark:border-[#27272a] hover:border-emerald-400 dark:hover:border-emerald-600 hover:shadow-md transition-all text-left"
              data-testid={`related-${key}`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${p.color}`}>
                <Icon className="w-4.5 h-4.5" />
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                  {p.labels[lang] || p.labels.sv}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">
                  {p.desc[lang] || p.desc.sv}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default RelatedPages;
